/**
 * Tournament state — phone-side. Tracks a running cross-game score in
 * localStorage so the same phone "carries" its total between tank,
 * quiz, and tilt. The mobile SDK announces this total on every pair-
 * up via a phone-context frame; each game can also award points
 * back to the phone via a game-score frame at the end of a round.
 */

const KEY = 'joystick:tournament';
/** The only name a tournament record may carry: three arcade initials. */
const INITIALS = /^[A-Z]{3}$/;

export interface TournamentState {
  total: number;
  /** Three arcade initials, A to Z. */
  name?: string;
}

export function loadTournament(): TournamentState {
  try {
    if (typeof localStorage === 'undefined') return { total: 0 };
    const raw = localStorage.getItem(KEY);
    if (!raw) return { total: 0 };
    const parsed = JSON.parse(raw) as Partial<TournamentState>;
    const total = typeof parsed.total === 'number' && Number.isFinite(parsed.total)
      ? Math.max(0, Math.floor(parsed.total))
      : 0;
    // Records from older versions may hold a longer free-text name: ignore it.
    const name = typeof parsed.name === 'string' && INITIALS.test(parsed.name) ? parsed.name : undefined;
    return name ? { total, name } : { total };
  } catch {
    return { total: 0 };
  }
}

export function saveTournament(state: TournamentState): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const payload: TournamentState = state.name && INITIALS.test(state.name)
      ? { total: Math.max(0, Math.floor(state.total)), name: state.name }
      : { total: Math.max(0, Math.floor(state.total)) };
    localStorage.setItem(KEY, JSON.stringify(payload));
    writeCrossSubdomainCookie(payload);
  } catch {
    /* private mode etc — fine to silently fail */
  }
}

/** Mirror the running tournament into a domain-wide cookie so the hub
 *  at play.forgesworn.dev can display a "your tournament: N pts" badge
 *  for phones that have already paired to a game. The cookie is
 *  scoped to .forgesworn.dev only when we're actually hosted there
 *  (no leak to localhost). Falls back silently if document.cookie is
 *  unavailable (private mode, opted-out browser). */
function writeCrossSubdomainCookie(state: TournamentState): void {
  try {
    if (typeof document === 'undefined') return;
    const host = location.hostname;
    // Only set the cross-subdomain cookie when we're on a
    // forgesworn.dev subdomain. Localhost / preview deploys keep the
    // value localStorage-only.
    if (!host.endsWith('.forgesworn.dev') && host !== 'forgesworn.dev') return;
    const value = encodeURIComponent(JSON.stringify(state));
    const maxAge = 60 * 60 * 24 * 30;   // 30 days
    document.cookie = `fg_tournament=${value};Domain=.forgesworn.dev;Path=/;Max-Age=${maxAge};SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

export function resetTournament(): TournamentState {
  const fresh: TournamentState = { total: 0 };
  saveTournament(fresh);
  return fresh;
}

export function addScore(delta: number): TournamentState {
  const current = loadTournament();
  const next: TournamentState = current.name
    ? { total: current.total + Math.max(0, Math.round(delta)), name: current.name }
    : { total: current.total + Math.max(0, Math.round(delta)) };
  saveTournament(next);
  return next;
}

/** Toast for a fresh score award. Floats in from the bottom, fades out
 *  after ~3s. Stacked toasts replace each other rather than queueing. */
export function showScoreToast(score: number, label: string | null, newTotal: number): void {
  let stack = document.getElementById('tournament-toast');
  if (stack) stack.remove();
  stack = document.createElement('div');
  stack.id = 'tournament-toast';
  stack.className = 'tournament-toast';
  const headline = document.createElement('div');
  headline.className = 'tournament-toast-headline';
  headline.textContent = score > 0 ? `+${score} POINTS` : 'NO POINTS';
  const sub = document.createElement('div');
  sub.className = 'tournament-toast-sub';
  sub.textContent = label ?? (score > 0 ? 'nice one' : 'better luck next round');
  const total = document.createElement('div');
  total.className = 'tournament-toast-total';
  total.textContent = `TOURNAMENT TOTAL: ${newTotal}`;
  stack.appendChild(headline);
  stack.appendChild(sub);
  stack.appendChild(total);
  (document.getElementById('app') ?? document.body).appendChild(stack);
  // Animate in on the next frame.
  requestAnimationFrame(() => stack!.classList.add('on'));
  // Fade and remove after ~3.2s.
  setTimeout(() => {
    if (!stack) return;
    stack.classList.remove('on');
    setTimeout(() => stack && stack.remove(), 320);
  }, 3000);
}

/** Record the player's initials in the tournament. Anything other than
 *  three capital letters is ignored and the name is dropped. */
export function setName(initials: string): TournamentState {
  const current = loadTournament();
  const next: TournamentState = INITIALS.test(initials)
    ? { total: current.total, name: initials }
    : { total: current.total };
  saveTournament(next);
  return next;
}

/** Render the tournament status panel inside the idle / scanner /
 *  install-first screens. Shows the running total, the player's
 *  initials, and a reset button. */
export function renderTournamentPanel(host: HTMLElement, onChange?: (state: TournamentState) => void): void {
  const state = loadTournament();
  const panel = document.createElement('div');
  panel.className = 'tournament-panel';

  const label = document.createElement('div');
  label.className = 'tournament-panel-label';
  label.textContent = 'TOURNAMENT';

  // The player's initials live on their account (see identity.ts / the
  // profile chip), so the panel is just the running total + reset.
  const total = document.createElement('div');
  total.className = 'tournament-panel-total';
  total.textContent = String(state.total);
  const hint = document.createElement('div');
  hint.className = 'tournament-panel-hint';
  hint.textContent = state.total > 0
    ? 'your running cross-game total · keep playing to climb'
    : 'play any game · points carry between tank, quiz, tilt';
  const reset = document.createElement('button');
  reset.className = 'tournament-panel-reset';
  reset.type = 'button';
  reset.textContent = state.total > 0 ? 'RESET' : '';
  reset.addEventListener('click', () => {
    const fresh = resetTournament();
    // Preserve the initials across a tournament reset.
    if (state.name) {
      const next = setName(state.name);
      total.textContent = String(next.total);
    } else {
      total.textContent = String(fresh.total);
    }
    hint.textContent = 'reset · your next game starts a fresh tournament';
    reset.textContent = '';
    onChange?.(loadTournament());
  });
  panel.appendChild(label);
  panel.appendChild(total);
  panel.appendChild(hint);
  if (state.total > 0) panel.appendChild(reset);
  host.appendChild(panel);
}
